import { Injectable, computed, signal } from '@angular/core';
import { RebalancingProposalEntity } from '../../domain/entities/rebalancing-proposal.entity';
import { RebalancingProposalGroupEntity } from '../../domain/entities/rebalancing-proposal-group.entity';
import { RebalancingProposalMember } from '../../domain/entities/rebalancing-proposal-member.entity';
import { TeamBalancingCandidateEntity } from '../../domain/entities/team-balancing-candidate.entity';
import { TeamWorkloadEntity } from '../../domain/entities/team-workload.entity';
import { TeamWorkloadSummaryEntity } from '../../domain/entities/team-workload-summary.entity';
import { WorkloadMemberEntity } from '../../domain/entities/workload-member.entity';
import { WorkloadViewMode } from '../../domain/enums/workload-view-mode.enum';

@Injectable({ providedIn: 'root' })
export class TeamWorkloadState {
  private readonly _summary = signal<TeamWorkloadSummaryEntity | null>(null);
  private readonly _overCapacityMembers = signal<WorkloadMemberEntity[]>([]);
  private readonly _overdueMembers = signal<WorkloadMemberEntity[]>([]);
  private readonly _atRiskMembers = signal<WorkloadMemberEntity[]>([]);
  private readonly _timeOffMembers = signal<WorkloadMemberEntity[]>([]);
  private readonly _teamWorkloads = signal<TeamWorkloadEntity[]>([]);
  private readonly _rebalancingProposals = signal<RebalancingProposalEntity[]>([]);
  private readonly _viewMode = signal<WorkloadViewMode>(WorkloadViewMode.Director);
  private readonly _includeClosedInvestigations = signal(false);
  private readonly _loadingWorkloadOverview = signal(false);
  private readonly _loadingRebalancing = signal(false);
  private readonly _error = signal<string | null>(null);

  private readonly _teamBalancingCandidates = signal<TeamBalancingCandidateEntity[]>([]);
  private readonly _loadingTeamBalancingCandidates = signal(false);
  private readonly _proposalGroups = signal<RebalancingProposalGroupEntity[]>([]);
  private readonly _removedProposalIds = signal<Set<string>>(new Set());
  private readonly _proposalOverrides = signal<Map<string, RebalancingProposalMember>>(new Map());
  private readonly _collapsedProposalTeams = signal<Set<string>>(new Set());
  private readonly _dismissedProposalIds = signal<Set<string>>(new Set());
  private readonly _applyingProposals = signal(false);

  public readonly summary = this._summary.asReadonly();
  public readonly overCapacityMembers = this._overCapacityMembers.asReadonly();
  public readonly overdueMembers = this._overdueMembers.asReadonly();
  public readonly atRiskMembers = this._atRiskMembers.asReadonly();
  public readonly timeOffMembers = this._timeOffMembers.asReadonly();
  public readonly teamWorkloads = this._teamWorkloads.asReadonly();
  public readonly rebalancingProposals = this._rebalancingProposals.asReadonly();
  public readonly viewMode = this._viewMode.asReadonly();
  public readonly includeClosedInvestigations = this._includeClosedInvestigations.asReadonly();
  public readonly loadingWorkloadOverview = this._loadingWorkloadOverview.asReadonly();
  public readonly loadingRebalancing = this._loadingRebalancing.asReadonly();
  public readonly error = this._error.asReadonly();

  public readonly teamBalancingCandidates = this._teamBalancingCandidates.asReadonly();
  public readonly loadingTeamBalancingCandidates = this._loadingTeamBalancingCandidates.asReadonly();
  public readonly proposalGroups = this._proposalGroups.asReadonly();
  public readonly removedProposalIds = this._removedProposalIds.asReadonly();
  public readonly proposalOverrides = this._proposalOverrides.asReadonly();
  public readonly collapsedProposalTeams = this._collapsedProposalTeams.asReadonly();
  public readonly dismissedProposalIds = this._dismissedProposalIds.asReadonly();
  public readonly applyingProposals = this._applyingProposals.asReadonly();

  public readonly effectiveProposals = computed(() => {
    const removed = this._removedProposalIds();
    return this._proposalGroups()
      .flatMap((g) => g.proposals)
      .filter((p) => !removed.has(p.investigationId));
  });

  public setSummary(summary: TeamWorkloadSummaryEntity | null): void { this._summary.set(summary); }
  public setOverCapacityMembers(members: WorkloadMemberEntity[]): void { this._overCapacityMembers.set(members); }
  public setOverdueMembers(members: WorkloadMemberEntity[]): void { this._overdueMembers.set(members); }
  public setAtRiskMembers(members: WorkloadMemberEntity[]): void { this._atRiskMembers.set(members); }
  public setTimeOffMembers(members: WorkloadMemberEntity[]): void { this._timeOffMembers.set(members); }
  public setTeamWorkloads(teams: TeamWorkloadEntity[]): void { this._teamWorkloads.set(teams); }
  public setRebalancingProposals(proposals: RebalancingProposalEntity[]): void { this._rebalancingProposals.set(proposals); }
  public setViewMode(mode: WorkloadViewMode): void { this._viewMode.set(mode); }
  public setIncludeClosedInvestigations(include: boolean): void { this._includeClosedInvestigations.set(include); }
  public setLoadingWorkloadOverview(loading: boolean): void { this._loadingWorkloadOverview.set(loading); }
  public setLoadingRebalancing(loading: boolean): void { this._loadingRebalancing.set(loading); }
  public setError(error: string | null): void { this._error.set(error); }

  public setTeamBalancingCandidates(candidates: TeamBalancingCandidateEntity[]): void { this._teamBalancingCandidates.set(candidates); }
  public setLoadingTeamBalancingCandidates(loading: boolean): void { this._loadingTeamBalancingCandidates.set(loading); }
  public setProposalGroups(groups: RebalancingProposalGroupEntity[]): void { this._proposalGroups.set(groups); }

  public removeProposal(investigationId: string): void {
    const next = new Set(this._removedProposalIds());
    next.add(investigationId);
    this._removedProposalIds.set(next);
  }

  public restoreProposal(investigationId: string): void {
    const next = new Set(this._removedProposalIds());
    next.delete(investigationId);
    this._removedProposalIds.set(next);
  }

  public setProposalOverride(investigationId: string, member: RebalancingProposalMember): void {
    const next = new Map(this._proposalOverrides());
    next.set(investigationId, member);
    this._proposalOverrides.set(next);
  }

  public toggleProposalTeamCollapsed(teamId: string): void {
    const next = new Set(this._collapsedProposalTeams());
    if (next.has(teamId)) { next.delete(teamId); } else { next.add(teamId); }
    this._collapsedProposalTeams.set(next);
  }

  public setApplyingProposals(applying: boolean): void { this._applyingProposals.set(applying); }

  public dismissProposal(investigationId: string): void {
    const nextRemoved = new Set(this._removedProposalIds());
    nextRemoved.add(investigationId);
    this._removedProposalIds.set(nextRemoved);
    const nextDismissed = new Set(this._dismissedProposalIds());
    nextDismissed.add(investigationId);
    this._dismissedProposalIds.set(nextDismissed);
  }

  public clearRebalancingSession(): void {
    this._proposalGroups.set([]);
    this._removedProposalIds.set(new Set());
    this._proposalOverrides.set(new Map());
    this._collapsedProposalTeams.set(new Set());
    this._dismissedProposalIds.set(new Set());
    this._applyingProposals.set(false);
    this._rebalancingProposals.set([]);
  }
}
